import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getTemplateComponent } from '../templates';
import buildBlobUrl from '../utils/buildBlobUrl';
import { getToken } from '../utils/auth';
import './SettingsPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DOC_TYPES = ['SLC', 'TC', 'BONAFIDE', 'RESULT', 'REPORT_CARD'];
const DOC_TYPE_LABELS = {
  SLC: 'School Leaving Certificate',
  TC: 'Transfer Certificate',
  BONAFIDE: 'Bonafide Certificate',
  RESULT: 'Results',
  REPORT_CARD: 'Report Cards',
};

const SettingsPage = () => {
  const { schoolId } = useParams();
  const [allTemplates, setAllTemplates] = useState([]);
  const [schoolConfig, setSchoolConfig] = useState([]);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null); // which docType is being assigned
  const [alert, setAlert] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // School info editing state
  const [editAddress, setEditAddress] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [infoSaving, setInfoSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const logoInputRef = useRef(null);

  const token = getToken(schoolId);
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, configRes, schoolRes] = await Promise.all([
        fetch(`${API_URL}/documents/templates`, { headers }),
        fetch(`${API_URL}/documents/school-config`, { headers }),
        fetch(`${API_URL}/school/info`, { headers }),
      ]);

      const templatesData = await templatesRes.json();
      const configData = await configRes.json();
      const schoolData = await schoolRes.json();

      if (templatesData.success) setAllTemplates(templatesData.data || []);
      if (configData.success) setSchoolConfig(configData.data || []);
      if (schoolData.success) {
        const info = schoolData.data;
        if (info.schoolLogo) info.schoolLogo = buildBlobUrl(info.schoolLogo);
        setSchoolInfo(info);
      }
    } catch {
      setAlert({ type: 'error', msg: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  // Sync edit fields when schoolInfo loads
  useEffect(() => {
    if (schoolInfo) {
      setEditAddress(schoolInfo.schoolAddress || '');
      setEditPhone(schoolInfo.schoolPhone || '');
    }
  }, [schoolInfo]);

  // Handle logo file selection
  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAlert({ type: 'error', msg: 'Please select an image file (PNG, JPG, SVG)' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAlert({ type: 'error', msg: 'File must be under 5MB' });
      return;
    }
    setSelectedLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setAlert(null);
  };

  // Upload logo
  const handleLogoUpload = async () => {
    if (!selectedLogoFile) return;
    setLogoUploading(true);
    setAlert(null);
    try {
      const formData = new FormData();
      formData.append('logo', selectedLogoFile);
      const res = await fetch(`${API_URL}/school/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setAlert({ type: 'success', msg: 'Logo updated successfully!' });
        setSelectedLogoFile(null);
        setLogoPreview(null);
        await loadData();
      } else {
        setAlert({ type: 'error', msg: data.message || 'Upload failed' });
      }
    } catch {
      setAlert({ type: 'error', msg: 'Failed to upload logo' });
    } finally {
      setLogoUploading(false);
    }
  };

  // Save school info (address, phone)
  const handleSaveInfo = async () => {
    setInfoSaving(true);
    setAlert(null);
    try {
      const res = await fetch(`${API_URL}/school/info`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ schoolAddress: editAddress, schoolPhone: editPhone }),
      });
      const data = await res.json();
      if (data.success) {
        setAlert({ type: 'success', msg: 'School info updated!' });
        await loadData();
      } else {
        setAlert({ type: 'error', msg: data.message || 'Failed to update' });
      }
    } catch {
      setAlert({ type: 'error', msg: 'Failed to update school info' });
    } finally {
      setInfoSaving(false);
    }
  };

  const handleAssign = async (docType, templateId) => {
    setAssigning(docType);
    setAlert(null);
    try {
      const res = await fetch(`${API_URL}/documents/school-config`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ docType, templateId }),
      });
      const data = await res.json();
      if (data.success) {
        setAlert({ type: 'success', msg: `Template assigned for ${DOC_TYPE_LABELS[docType]}` });
        await loadData();
      } else {
        setAlert({ type: 'error', msg: data.message || 'Failed' });
      }
    } catch {
      setAlert({ type: 'error', msg: 'Failed to assign template' });
    } finally {
      setAssigning(null);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="dash-spinner" />
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <header className="dash-topbar">
        <div>
          <h1 className="page-title">Template Settings</h1>
          <p className="page-subtitle">Select which document design each type should use</p>
        </div>
      </header>

      {alert && (
        <div className={`docgen-alert docgen-alert-${alert.type}`}>
          <span>{alert.msg}</span>
          <button onClick={() => setAlert(null)}>&times;</button>
        </div>
      )}

      {/* School Info & Logo Section */}
      <div className="settings-school-section">
        <h3 className="settings-section-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          School Information &amp; Logo
        </h3>

        <div className="settings-school-grid">
          {/* Logo Column */}
          <div className="settings-logo-col">
            <div className="settings-logo-box">
              {logoPreview ? (
                <img src={logoPreview} alt="Preview" />
              ) : schoolInfo?.schoolLogo ? (
                <img src={schoolInfo.schoolLogo} alt="School Logo" />
              ) : (
                <div className="settings-logo-placeholder">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span>No Logo</span>
                </div>
              )}
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
              onChange={handleLogoSelect}
              style={{ display: 'none' }}
            />
            <button className="docgen-btn docgen-btn-outline" onClick={() => logoInputRef.current?.click()}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Choose Logo
            </button>
            {selectedLogoFile && (
              <div className="settings-logo-actions">
                <span className="settings-logo-filename">{selectedLogoFile.name}</span>
                <button
                  className="docgen-btn docgen-btn-primary"
                  onClick={handleLogoUpload}
                  disabled={logoUploading}
                >
                  {logoUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            )}
            <span className="settings-logo-hint">PNG, JPG, SVG, WEBP &bull; Max 5MB</span>
          </div>

          {/* Info Column */}
          <div className="settings-info-col">
            <div className="settings-info-field">
              <label>School Name</label>
              <div className="settings-info-readonly">{schoolInfo?.schoolName || '—'}</div>
            </div>
            <div className="settings-info-field">
              <label>School Code</label>
              <div className="settings-info-readonly">{schoolInfo?.schoolCode || '—'}</div>
            </div>
            <div className="settings-info-field">
              <label>Address</label>
              <textarea
                value={editAddress}
                onChange={(e) => setEditAddress(e.target.value)}
                rows={2}
                placeholder="Enter school address"
              />
            </div>
            <div className="settings-info-field">
              <label>Email</label>
              <div className="settings-info-readonly">{schoolInfo?.schoolEmail || '—'}</div>
            </div>
            <div className="settings-info-field">
              <label>Phone</label>
              <input
                type="text"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                placeholder="Enter phone number"
              />
            </div>
            <button
              className="docgen-btn docgen-btn-primary"
              onClick={handleSaveInfo}
              disabled={infoSaving}
            >
              {infoSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Doc Type Sections */}
      {DOC_TYPES.map(dt => {
        const config = schoolConfig.find(c => c.docType === dt);
        const currentTemplateId = config?.templateId;
        const availableTemplates = allTemplates.filter(t => t.Doc_Type === dt);

        return (
          <div key={dt} className="settings-doctype-section">
            <div className="settings-doctype-header">
              <div>
                <h3 className="settings-doctype-title">{DOC_TYPE_LABELS[dt]}</h3>
                <span className="settings-doctype-current">
                  {currentTemplateId
                    ? <>Active: <strong>{availableTemplates.find(t => t.Template_Id === currentTemplateId)?.Template_Name || currentTemplateId}</strong></>
                    : 'No template assigned'
                  }
                </span>
              </div>
            </div>

            <div className="settings-templates-row">
              {availableTemplates.length > 0 ? (
                availableTemplates.map(tpl => {
                  const isActive = currentTemplateId === tpl.Template_Id;
                  const hasPreview = !!getTemplateComponent(tpl.Template_Id);
                  return (
                    <div key={tpl.Template_Id} className={`settings-template-card ${isActive ? 'settings-template-active' : ''}`}>
                      {/* Preview thumbnail */}
                      <div
                        className="settings-template-preview"
                        onClick={() => hasPreview && setPreviewTemplate(tpl.Template_Id)}
                        style={{ cursor: hasPreview ? 'pointer' : 'default' }}
                      >
                        {hasPreview ? (
                          <div className="settings-template-mini">
                            {React.createElement(getTemplateComponent(tpl.Template_Id), {
                              data: { Student_Name: 'John Doe', Father_Name: 'James Doe', Mother_Name: 'Jane Doe', Student_Class: '10', Student_Section: 'A', Certificate_Number: 'SAMPLE/001' },
                              schoolInfo: schoolInfo || { schoolName: 'Sample School' },
                            })}
                          </div>
                        ) : (
                          <div className="settings-template-placeholder">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                            <span>Coming soon</span>
                          </div>
                        )}
                      </div>

                      <div className="settings-template-info">
                        <strong>{tpl.Template_Name}</strong>
                        <span>{tpl.Description || ''}</span>
                        {tpl.Is_Default ? <span className="settings-default-tag">Default</span> : null}
                      </div>

                      <button
                        className={`docgen-btn ${isActive ? 'settings-btn-active' : 'docgen-btn-primary'}`}
                        onClick={() => handleAssign(dt, tpl.Template_Id)}
                        disabled={isActive || assigning === dt}
                      >
                        {assigning === dt ? 'Applying...' : isActive ? 'Selected' : 'Use This Design'}
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="settings-no-templates">
                  <p>No templates available for this document type yet.</p>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Full Preview Modal */}
      {previewTemplate && (
        <div className="settings-preview-modal" onClick={() => setPreviewTemplate(null)}>
          <div className="settings-preview-content" onClick={e => e.stopPropagation()}>
            <button className="settings-preview-close" onClick={() => setPreviewTemplate(null)}>&times;</button>
            <div className="settings-preview-scroll">
              {React.createElement(getTemplateComponent(previewTemplate), {
                data: {
                  Student_Name: 'Ravi Kumar', Father_Name: 'Suresh Kumar', Mother_Name: 'Priya Kumar',
                  Guardian_Name: '', Date_Of_Birth: '2010-05-15', Gender: 'Male', Nationality: 'Indian',
                  Religion: 'Hindu', Category: 'General', Admission_Number: 'ADM/2020/001',
                  Student_Class: '10', Student_Section: 'A', Class_Last_Studied: '10',
                  Section_Last_Studied: 'A', Conduct: 'Good', Certificate_Number: 'SAMPLE/2024/001',
                  Certificate_Date: new Date().toISOString().split('T')[0],
                  Academic_Session: '2024-25', Purpose: 'Higher Studies',
                  All_Dues_Cleared: 'Yes', Qualified_For_Promotion: 'Yes',
                },
                schoolInfo: schoolInfo || { schoolName: 'Sample School' },
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
